import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  Chip,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import useReportStore from '../stores/useReportStore';
import useAuthStore from '../stores/useAuthStore';
import { columnsConfig } from '../columnsConfig'; // Yukarıdaki tanım
import ExportExcelButton from '../components/ExportExcelButton';


function LogReportPage() {
  const { logData, isLoading, error, fetchLogs } = useReportStore();
  const customerId = useAuthStore((state) => state.customerId);

  // Tüm kolonlar için, field'e göre string değer saklarız.
  const [filters, setFilters] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchLogs(customerId);
  }, [customerId, fetchLogs]);

  const handleRefresh = () => {
    fetchLogs(customerId);
  };

  // Değer değiştiğinde, ilgili field için filtre değerini güncelliyoruz.
  const handleFilterChange = (field: string, newValue: string) => {
    setFilters((prev) => ({ ...prev, [field]: newValue }));
  };

  // Asıl filtreleme işlemi; filters objesindeki her bir field -> value ikilisine bakarak satırları eliyoruz.
  const filteredData = useMemo(() => {
    return logData.filter((row: any) => {
      // Tüm filter field'larını kontrol edelim
      for (const field in filters) {
        const filterValue = filters[field].toLowerCase();
        if (filterValue) {
          const cellValue = String(row[field] ?? '').toLowerCase();
          if (!cellValue.includes(filterValue)) {
            return false; // eşleşme yoksa satırı eliyoruz
          }
        }
      }
      return true; // Tüm filtrelerden geçtiyse satırı dahil ediyoruz
    });
  }, [logData, filters]);

  const formatters: Record<string, (val: any) => string> = {
    createDate: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
    // Add more if you want to convert chips into text, like:
    status: (val) => {
      if (val === 0) return 'İşlem Başladı';
      if (val === 1) return 'İşlem Bitti';
      return String(val);
    },
    bankRecord: (val) => {
      if (val === '00') return 'Başarılı';
      if (val === '99') return 'Başarısız';
      return String(val);
    },
    isRecordErp: (val) => (val ? 'Evet' : 'Hayır'),
    amount: (val: any) =>
      Number(val).toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  };
  
  // columnsConfig üzerinde dolaşıp, eğer 'filterable' ise, 
  // renderHeader ile TextField ekliyoruz. 
  // Diğer özel renderCell'leri de handle ediyoruz.
  const dynamicColumns: GridColDef[] = columnsConfig.map((col: GridColDef) => {
    const base: GridColDef = {
      ...col,
    };

    // Tarih formatlamak isterseniz
    if (col.field === 'createDate') {
      base.valueFormatter = (params:any) =>
        dayjs(params).format('DD.MM.YYYY HH:mm');
    }

    // bankRecord vs. gibi özel durumlarda renderCell yazabilirsiniz.
    if (col.field === 'bankRecord') {
      base.renderCell = (params) => {
        if (params.value === '00') {
          return <Chip label="Başarılı" color="success" />;
        } else if (params.value === '99') {
          return <Chip label="Başarısız" color="error" />;
        }
        return <Chip label={params.value} variant="outlined" />;
      };
    }

    if (col.field === 'isRecordErp') {
      base.renderCell = (params) =>
        params.value ? (
          <Chip label="✅" color="success" variant="outlined" />
        ) : (
          <Chip label="❌" color="error" variant="outlined" />
        );
    }

    if (col.field === 'status') {
      base.renderCell = (params) => {
        if (params.value === 0) {
          return <Chip label="İşlem Başladı" color="success" />;
        } else if (params.value === 1) {
          return <Chip label="İşlem Bitti" color="warning" />;
        }
        return <Chip label={params.value} variant="outlined" />;
      };
    }

    // Eğer filtrelenebilir bir kolon ise, renderHeader ekliyoruz
    if (col.filterable) {
      base.renderHeader = () => (
        <Box>
          <Typography variant="subtitle2">{col.headerName}</Typography>
          <TextField
            variant="standard"
            size="small"
            // filters'ta bu field'a ait değer
            value={filters[col.field] || ''}
            onChange={(e) => handleFilterChange(col.field, e.target.value)}
            placeholder="Ara..."
            autoComplete="off"
          />
        </Box>
      );
    }

    return base;
  });

  return (
    <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Üst Kısım (Butonlar vs.) */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="contained" onClick={handleRefresh}>
          Yenile
        </Button>
        <ExportExcelButton
  rows={filteredData}
  columns={dynamicColumns.map((col) => ({
    field: col.field,
    headerName: col.headerName ?? col.field,
  }))}
  fileName="Log_Raporu"
  formatters={formatters}
/>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {isLoading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Yükleniyor...
          </Alert>
        )}
      </Box>

      {/* Tablo Kısmı */}
      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          rows={filteredData}
          columns={dynamicColumns}
          getRowId={(row) => row.id}
          loading={isLoading}
          sx={{ height: '100%', width: '100%' }}
          slots={{
            noRowsOverlay: () => (
              <Typography p={2} color="text.secondary" textAlign="center">
                Görüntülenecek log verisi bulunamadı.
              </Typography>
            ),
          }}
        />
      </Box>
    </Box>
  );
}

export default LogReportPage;
